import React from "react";
import { colors } from '../../../../../theme/colors';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import UploadFile from "../../create/UploadFile";
import {
  CustomPaper,
  CustomPhoneInput,
  RightSection,
  UploadBox,
  UploadJDContainer,
  UploadResumeContainer,
} from "./style";
import UploadProfilePic from "../../create/UploadProfilePic";

import {
  CustomSelect,
  CustomSelectField,
} from "../../../../../components/common-components/CustomSelect";
import FieldErrorMessage from "../../../../../components/common-components/FieldErrorMessage";

import { FormContainer } from "./style";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useEditCandidateInfo from "./useEditCandidateInfo";
import { applyCurrentTimeIfToday } from "../../adjustPickerDateForToday";

const EditCandidateInfo = ({ selectedInterviewData }) => {
  const {
    formik,
    handleCandidateInterview,
    handleNavigateCandidateList,
    handleInputChange,
    handleGetMoreUserOnScroll,
    handleSetProfilePic,
    uploadProfilePicClasses,
    profilePic,
    setProfilePic,
    allRoles,
    allRecruiterList,
    recruiter,
    setRecruiter,
    allUserList,
    startDate,
    setStartDate,
    now,
    validatePhoneNumber,
    validPhoneNumber,
    phomeErrorMessage,
    useUpdateInterviewMutation,
    inputRef,
    clearProfilePic,
    setFieldChanged,
    isUpdating,
    levelOptions,
    languageOptions,
    interviewTypeOptions,
  } = useEditCandidateInfo(selectedInterviewData);

  return (
    <FormContainer component="form" onSubmit={formik.handleSubmit}>
      <Box sx={{ mb: 2.5, pb: 1.5, borderBottom: `1px solid ${colors.border.light}` }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary }}>
          Edit Scheduled Interview
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
          Update candidate profile, resume, job details, and interview configuration
        </Typography>
      </Box>
      <Box sx={{ padding: "0.5rem 0" }}>
        <Grid item xs={12}>
          <CustomPaper>
            <UploadBox>
              {selectedInterviewData ? (
                <UploadProfilePic
                  className={uploadProfilePicClasses}
                  getProfilePic={handleSetProfilePic}
                  profilePic={profilePic}
                  setProfilePic={setProfilePic}
                  inputRef={inputRef}
                  clearProfilePic={clearProfilePic}
                />
              ) : (
                <UploadProfilePic />
              )}
              <Typography variant="body2" className="file_format">
                Allowed *.jpeg, *.jpg, *.png
                <br />
                max size of 3 Mb
              </Typography>
            </UploadBox>
          </CustomPaper>
        </Grid>
        <UploadResumeContainer>
          <UploadFile
            label="Resume"
            filename={formik.values.resume ? formik.values.resume : null}
            handleGetFile={(file) => {
              formik.setFieldValue("resume", file.name);
              setFieldChanged((prev) => ({
                ...prev,
                resume: file,
              }));
            }}
          />
        </UploadResumeContainer>
        <UploadJDContainer>
          <UploadFile
            label="Job Description"
            filename={
              formik.values.jobDescription ? formik.values.jobDescription : null
            }
            handleGetFile={(file) => {
              formik.setFieldValue("jobDescription", file.name);
              setFieldChanged((prev) => ({
                ...prev,
                jobDescription: file,
              }));
            }}
          />
        </UploadJDContainer>

        <Grid item xs={12} md={8}>
          <RightSection>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Full name*"
                  placeholder="Full name"
                  variant="outlined"
                  id="candidateName"
                  name="candidateName"
                  value={formik.values.candidateName}
                  onChange={handleCandidateInterview}
                  disabled={!selectedInterviewData || isUpdating}
                />
                <FieldErrorMessage error={formik.errors.candidateName} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Email address*"
                  type="email"
                  variant="outlined"
                  id="candidateEmail"
                  name="candidateEmail"
                  value={formik.values.candidateEmail}
                  onChange={handleCandidateInterview}
                  disabled={!selectedInterviewData || isUpdating}
                />
                <FieldErrorMessage error={formik.errors.candidateEmail} />
              </Grid>
              <Grid item xs={6}>
                <CustomPhoneInput
                  country={"us"}
                  inputStyle={{ width: "100%" }}
                  onChange={(phone, country) => {
                    validatePhoneNumber(phone, country);
                  }}
                  value={formik.values.candidatePhone}
                  inputProps={{
                    name: "candidatePhone",
                    id: "candidatePhone",
                    autoComplete: "off",
                  }}
                  disabled={!selectedInterviewData || isUpdating}
                />
                <FieldErrorMessage
                  error={
                    formik.errors.candidatePhone
                      ? formik.errors.candidatePhone
                      : phomeErrorMessage
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <CustomSelect
                  options={allRoles.data?.data || []}
                  getOptionLabel={(option) => {
                    return option?.role;
                  }}
                  value={formik.values.applyFor}
                  onChange={(event, value) => {
                    formik.setFieldValue("applyFor", value);
                    setFieldChanged((prev) => ({
                      ...prev,
                      applyFor: value,
                    }));
                  }}
                  disabled={!selectedInterviewData || isUpdating}
                  renderInput={(params) => (
                    <CustomSelectField
                      {...params}
                      label="Apply for*"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {allRoles.isLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem
                      {...props}
                      sx={{
                        "&:hover": {
                          backgroundColor: `${colors.primary} !important`,
                          color: "#fff",
                        },
                      }}
                    >
                      {option.role || ""}
                    </MenuItem>
                  )}
                />
                <FieldErrorMessage error={formik.errors.applyFor} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="State"
                  variant="outlined"
                  id="state"
                  name="state"
                  value={formik.values.state}
                  onChange={handleCandidateInterview}
                  disabled={!selectedInterviewData || isUpdating}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="City"
                  variant="outlined"
                  id="city"
                  name="city"
                  value={formik.values.city}
                  onChange={handleCandidateInterview}
                  disabled={!selectedInterviewData || isUpdating}
                />
                <FieldErrorMessage error={formik.errors.city} />
              </Grid>
              <Grid item xs={6}>
                <CustomSelect
                  options={allRecruiterList || []}
                  getOptionLabel={(option) => option?.name || ""}
                  value={recruiter}
                  onInputChange={handleInputChange}
                  disabled={!selectedInterviewData || isUpdating}
                  onBlur={(e) => {
                    if (allRecruiterList.length === 0) {
                      setRecruiter(null);
                      formik.setFieldValue("recruiter", "");
                      formik.setFieldValue("recruiterEmail", "");
                      setFieldChanged((prev) => ({
                        ...prev,
                        recruiter: {
                          recruiter: "",
                          recruiterEmail: "",
                        },
                      }));
                    }
                  }}
                  onChange={(event, value) => {
                    if (value) {
                      setRecruiter(value);
                      setFieldChanged((prev) => ({
                        ...prev,
                        recruiter: {
                          recruiter: value.name,
                          recruiterEmail: value.email,
                        },
                      }));
                      formik.setFieldValue("recruiter", value.name);
                      formik.setFieldValue("recruiterEmail", value.email);
                    } else {
                      setRecruiter(null);
                      formik.setFieldValue("recruiter", "");
                      formik.setFieldValue("recruiterEmail", "");
                      setFieldChanged((prev) => ({
                        ...prev,
                        recruiter: {
                          recruiter: "",
                          recruiterEmail: "",
                        },
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <CustomSelectField
                      {...params}
                      label="Recruiter*"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {allUserList.isLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem
                      {...props}
                      sx={{
                        "&:hover": {
                          backgroundColor: `${colors.primary} !important`,
                          color: "#fff",
                        },
                      }}
                    >
                      {option.name || ""}
                    </MenuItem>
                  )}
                  ListboxProps={{
                    onScroll: handleGetMoreUserOnScroll,
                    style: {
                      maxHeight: 150,
                      overflow: "auto",
                    },
                  }}
                />
                <FieldErrorMessage error={formik.errors.recruiter} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Recruiter Email*"
                  variant="outlined"
                  id="recruiterEmail"
                  name="recruiterEmail"
                  value={formik.values.recruiterEmail}
                  disabled={!selectedInterviewData || isUpdating}
                />
                <FieldErrorMessage error={formik.errors.recruiterEmail} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Street"
                  variant="outlined"
                  id="street"
                  name="street"
                  value={formik.values.street}
                  onChange={handleCandidateInterview}
                  disabled={!selectedInterviewData || isUpdating}
                />
                <FieldErrorMessage error={formik.errors.street} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Zip code"
                  variant="outlined"
                  id="zip"
                  name="zip"
                  value={formik.values.zip}
                  onChange={handleCandidateInterview}
                  disabled={!selectedInterviewData || isUpdating}
                />
                <FieldErrorMessage error={formik.errors.zip} />
              </Grid>

              <Grid item xs={6}>
                <DatePicker
                  name="startTime"
                  placeholderText="Start Time*"
                  selected={startDate}
                  autoComplete="off"
                  disabled={!selectedInterviewData || isUpdating}
                  onChange={(date) => {
                    const next = date
                      ? applyCurrentTimeIfToday(date)
                      : null;
                    if (next) {
                      const autoEnd = new Date(next.getTime() + 48 * 60 * 60 * 1000);
                      formik.setFieldValue("startTime", next.toISOString());
                      formik.setFieldValue("endTime", autoEnd.toISOString());
                      setFieldChanged((prev) => ({
                        ...prev,
                        startTime: next.toISOString(),
                        endTime: autoEnd.toISOString(),
                      }));
                    }
                    setStartDate(next);
                  }}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={1}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  minDate={now}
                  minTime={(() => {
                    const today = new Date();
                    if (!startDate) return today;
                    const startIsToday =
                      startDate.getFullYear() === today.getFullYear() &&
                      startDate.getMonth() === today.getMonth() &&
                      startDate.getDate() === today.getDate();
                    return startIsToday
                      ? today
                      : new Date(0, 0, 0, 0, 0);
                  })()}
                  maxTime={(() => {
                    const t = new Date();
                    return new Date(
                      t.getFullYear(),
                      t.getMonth(),
                      t.getDate(),
                      23,
                      59,
                    );
                  })()}
                />
                <FieldErrorMessage error={formik.errors.startTime} />
              </Grid>

              <Grid item xs={6}>
                <CustomSelect
                  fullWidth
                  options={[
                    { label: "Yes", value: "yes" },
                    { label: "No", value: "no" },
                  ]}
                  getOptionLabel={(option) => option.label}
                  value={
                    formik.values.useCustomQuestion
                      ? {
                        label:
                          formik.values.useCustomQuestion === "yes"
                            ? "Yes"
                            : "No",
                        value: formik.values.useCustomQuestion,
                      }
                      : null
                  }
                  onChange={(event, value) => {
                    formik.setFieldValue(
                      "useCustomQuestion",
                      value ? value.value : ""
                    );
                    setFieldChanged((prev) => ({
                      ...prev,
                      useCustomQuestion: value ? value.value : "",
                    }));
                    if (value?.value !== "yes") {
                      formik.setFieldValue("experienceLevel", "");
                      setFieldChanged((prev) => ({
                        ...prev,
                        experienceLevel: "",
                      }));
                    }
                  }}
                  onBlur={() =>
                    formik.setFieldTouched("useCustomQuestion", true)
                  }
                  disabled={!selectedInterviewData || isUpdating}
                  renderInput={(params) => (
                    <CustomSelectField
                      {...params}
                      label="Custom Question*"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem
                      {...props}
                      sx={{
                        "&:hover": {
                          backgroundColor: `${colors.primary} !important`,
                          color: "#fff",
                        },
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  )}
                />
                <FieldErrorMessage error={formik.errors.useCustomQuestion} />
              </Grid>

              <Grid item xs={6}>
                <CustomSelect
                  fullWidth
                  options={languageOptions}
                  getOptionLabel={(option) => option?.label || ""}
                  value={
                    languageOptions.find(
                      (opt) => opt.value === formik.values.interviewLanguage,
                    ) || null
                  }
                  onChange={(event, value) => {
                    const code = value?.value || "en";
                    formik.setFieldValue("interviewLanguage", code);
                    setFieldChanged((prev) => ({
                      ...prev,
                      interviewLanguage: code,
                    }));
                  }}
                  disabled={!selectedInterviewData || isUpdating}
                  renderInput={(params) => (
                    <CustomSelectField
                      {...params}
                      label="Interview Language"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem
                      {...props}
                      sx={{
                        "&:hover": {
                          backgroundColor: `${colors.primary} !important`,
                          color: "#fff",
                        },
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  )}
                />
              </Grid>

              {formik.values.useCustomQuestion === "yes" && (
                <Grid item xs={6}>
                  <CustomSelect
                    fullWidth
                    options={levelOptions}
                    getOptionLabel={(option) => option?.label || option || ""}
                    value={
                      formik.values.experienceLevel
                        ? levelOptions.find(
                            (opt) => opt.value === formik.values.experienceLevel
                          ) || null
                        : null
                    }
                    onChange={(event, value) => {
                      formik.setFieldValue(
                        "experienceLevel",
                        value?.value || value || ""
                      );
                      setFieldChanged((prev) => ({
                        ...prev,
                        experienceLevel: value?.value || value || "",
                      }));
                    }}
                    disabled={!selectedInterviewData || isUpdating}
                    renderInput={(params) => (
                      <CustomSelectField
                        {...params}
                        label="Experience Level"
                        variant="outlined"
                        fullWidth
                      />
                    )}
                    renderOption={(props, option) => (
                      <MenuItem
                        {...props}
                        sx={{
                          "&:hover": {
                            backgroundColor: `${colors.primary} !important`,
                            color: "#fff",
                          },
                        }}
                      >
                        {option.label || ""}
                      </MenuItem>
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={6}>
                <CustomSelect
                  fullWidth
                  options={interviewTypeOptions}
                  getOptionLabel={(option) => option?.label || option || ""}
                  value={
                    formik.values.interviewType
                      ? interviewTypeOptions.find(
                          (opt) => opt.value === formik.values.interviewType
                        ) || null
                      : null
                  }
                  onChange={(event, value) => {
                    formik.setFieldValue(
                      "interviewType",
                      value?.value || ""
                    );
                    setFieldChanged((prev) => ({
                      ...prev,
                      interviewType: value?.value || "",
                    }));
                  }}
                  disabled={!selectedInterviewData || isUpdating}
                  renderInput={(params) => (
                    <CustomSelectField
                      {...params}
                      label="Interview Type*"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem
                      {...props}
                      sx={{
                        "&:hover": {
                          backgroundColor: `${colors.primary} !important`,
                          color: "#fff",
                        },
                      }}
                    >
                      {option.label || ""}
                    </MenuItem>
                  )}
                />
                <FieldErrorMessage error={formik.errors.interviewType} />
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                  marginTop: "26px",
                  marginBottom: "16px",
                }}
              >
                <Button
                  variant="contained"
                  className="cancel-btn"
                  disabled={!selectedInterviewData}
                  onClick={handleNavigateCandidateList}
                  sx={{ marginLeft: "12px" }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  className="update-btn"
                  type="submit"
                  disabled={!selectedInterviewData || isUpdating}
                  sx={{ marginLeft: "12px" }}
                >
                  {isUpdating ? (
                    <>
                      Updating Information
                      <CircularProgress
                        size={20}
                        color="inherit"
                        sx={{ marginLeft: 1 }}
                      />
                    </>
                  ) : (
                    "Update Information"
                  )}
                </Button>
              </Box>
            </Grid>
          </RightSection>
        </Grid>
      </Box>
    </FormContainer>
  );
};

export default EditCandidateInfo;
